import { useSearchParams, useNavigate } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import api from '../api/client';
import MarkdownText from '../components/MarkdownText';
import { shuffleArray } from '../utils/workAttemptHelpers';
import { studentLabelWithLogin } from '../utils/studentLabel';

const TASK_STATUS_OPTIONS = [
  { value: '', label: 'Любой статус' },
  { value: 'published', label: 'Опубликовано' },
  { value: 'draft', label: 'Черновик' },
  { value: 'archived', label: 'Архив' },
];

/**
 * Страница создания списка задач или заготовки:
 * - Режим «список для ученика» или «заготовка» (без ученика).
 * - Слева: фильтр + список задач (карточки). Справа: формируемый список (сортируемый).
 */
export default function TeacherListCreatePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const mode = searchParams.get('mode') === 'template' ? 'template' : 'student';
  const initialStudentId = searchParams.get('student_id') || '';

  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [studentId, setStudentId] = useState(initialStudentId);
  const [deadline, setDeadline] = useState('');
  const [templateTagIds, setTemplateTagIds] = useState([]);
  const [draftItems, setDraftItems] = useState([]); // { taskId, slug, title, statement?, source_number?, source_name?, tags?, teacherNote, collapsed }
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Left column: filter and tasks
  const [filterSearch, setFilterSearch] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksError, setTasksError] = useState('');
  const [leftCollapsed, setLeftCollapsed] = useState({}); // taskId -> true if collapsed
  const [leftExpandAll, setLeftExpandAll] = useState(true); // true = currently expanded all
  const [tags, setTags] = useState([]);

  // Right column: expand all
  const [_rightExpandAll, setRightExpandAll] = useState(false);

  // Templates for "load from template"
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [shuffleQuestions, setShuffleQuestions] = useState(false);

  useEffect(() => {
    if (mode === 'student') {
      api.get('/teacher/students/').then((res) => {
        const data = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
        setStudents(data);
        if (initialStudentId && !studentId) setStudentId(initialStudentId);
      }).catch(() => {}).finally(() => setStudentsLoading(false));
    } else {
      setStudentsLoading(false);
    }
  }, [mode, initialStudentId]);

  useEffect(() => {
    api.get('/tasks/editor/tags/').then((res) => {
      const data = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
      setTags(data);
    }).catch(() => {});
  }, []);

  const fetchTasks = useCallback(() => {
    setTasksLoading(true);
    setTasksError('');
    const params = new URLSearchParams();
    if (filterSearch.trim()) params.set('search', filterSearch.trim());
    if (filterTag) params.set('tag', filterTag);
    if (filterStatus) params.set('status', filterStatus);
    api.get(`/teacher/tasks-for-list/?${params.toString()}`).then((res) => {
      setTasks(Array.isArray(res.data) ? res.data : res.data?.results ?? []);
    }).catch(() => {
      setTasksError('Не удалось загрузить задачи.');
      setTasks([]);
    }).finally(() => setTasksLoading(false));
  }, [filterSearch, filterTag, filterStatus]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  useEffect(() => {
    setTemplatesLoading(true);
    api.get('/teacher/task-lists/?is_template=1').then((res) => {
      setTemplates(Array.isArray(res.data) ? res.data : res.data?.results ?? []);
    }).catch(() => setTemplates([])).finally(() => setTemplatesLoading(false));
  }, []);

  const draftTaskIds = new Set(draftItems.map((it) => it.taskId));

  const addToDraft = (task) => {
    if (!task?.id || draftTaskIds.has(task.id)) return;
    setDraftItems((prev) => [
      ...prev,
      {
        taskId: task.id,
        slug: task.slug,
        title: task.title,
        statement: task.statement,
        source_number: task.source_number,
        source_name: task.source_name,
        tags: task.tags || [],
        teacherNote: '',
        collapsed: true,
      },
    ]);
  };

  const removeFromDraft = (taskId) => {
    setDraftItems((prev) => prev.filter((it) => it.taskId !== taskId));
  };

  const updateDraftNote = (taskId, note) => {
    setDraftItems((prev) => prev.map((it) => (it.taskId === taskId ? { ...it, teacherNote: note } : it)));
  };

  const setDraftItemCollapsed = (taskId, collapsed) => {
    setDraftItems((prev) => prev.map((it) => (it.taskId === taskId ? { ...it, collapsed } : it)));
  };

  const moveDraftItem = (index, direction) => {
    const next = index + direction;
    if (next < 0 || next >= draftItems.length) return;
    setDraftItems((prev) => {
      const arr = [...prev];
      [arr[index], arr[next]] = [arr[next], arr[index]];
      return arr;
    });
  };

  const setLeftCardCollapsed = (taskId, collapsed) => {
    setLeftCollapsed((prev) => ({ ...prev, [taskId]: collapsed }));
  };

  const collapseAllLeft = () => {
    setLeftCollapsed((prev) => {
      const next = { ...prev };
      tasks.forEach((t) => { next[t.id] = true; });
      return next;
    });
    setLeftExpandAll(false);
  };

  const expandAllLeft = () => {
    setLeftCollapsed({});
    setLeftExpandAll(true);
  };

  const collapseAllRight = () => {
    setDraftItems((prev) => prev.map((it) => ({ ...it, collapsed: true })));
    setRightExpandAll(false);
  };

  const expandAllRight = () => {
    setDraftItems((prev) => prev.map((it) => ({ ...it, collapsed: false })));
    setRightExpandAll(true);
  };

  const loadFromTemplate = () => {
    if (!selectedTemplateId) return;
    setTemplatesLoading(true);
    api.get(`/teacher/task-lists/${selectedTemplateId}/`).then((res) => {
      const list = res.data;
      const items = Array.isArray(list.items) ? list.items : [];
      const newItems = items.map((it) => ({
        taskId: it.task?.id,
        slug: it.task?.slug,
        title: it.task?.title,
        statement: it.task?.statement,
        source_number: it.task?.source_number,
        source_name: it.task?.source_name,
        tags: it.task?.tags || [],
        teacherNote: it.teacher_note || '',
        collapsed: true,
      })).filter((it) => it.taskId);
      setDraftItems((prev) => {
        const seen = new Set(prev.map((x) => x.taskId));
        const added = newItems.filter((it) => !seen.has(it.taskId));
        added.forEach((it) => seen.add(it.taskId));
        return [...prev, ...added];
      });
    }).catch(() => {}).finally(() => setTemplatesLoading(false));
  };

  const handleSave = async () => {
    if (!title.trim()) {
      setSaveError('Введите название списка.');
      return;
    }
    if (mode === 'student' && !studentId) {
      setSaveError('Выберите ученика.');
      return;
    }
    setSaveError('');
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        deadline_at: mode === 'student' && deadline ? `${deadline}T23:59:59` : null,
        student: mode === 'student' ? Number(studentId) : null,
        tags: mode === 'template' && templateTagIds.length > 0 ? templateTagIds : undefined,
        items: (mode === 'student' && shuffleQuestions ? shuffleArray(draftItems) : draftItems).map(
          (it, index) => ({
            task_id: it.taskId,
            position: index,
            teacher_note: it.teacherNote || '',
          }),
        ),
      };
      await api.post('/teacher/task-lists/', payload);
      navigate('/teacher', { replace: true });
    } catch (e) {
      const msg = e.response?.data;
      if (typeof msg === 'object' && msg !== null) {
        const first = Object.entries(msg)[0];
        setSaveError(first ? `${first[0]}: ${Array.isArray(first[1]) ? first[1][0] : first[1]}` : 'Ошибка сохранения.');
      } else {
        setSaveError('Ошибка сохранения списка.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="teacher-list-create-page">
      <header className="teacher-list-create-header">
        <button
          type="button"
          className="btn btn-outline btn-sm"
          onClick={() => navigate('/teacher')}
        >
          ← К кабинету
        </button>
        <h1>{mode === 'template' ? 'Создание заготовки' : 'Создание списка для ученика'}</h1>
      </header>

      <div className="teacher-list-create-top">
        <div className="form-row form-row-inline">
          <div className="form-group">
            <label>Название</label>
            <input
              type="text"
              className="form-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={mode === 'template' ? 'Название заготовки' : 'Например, ДЗ 12/03'}
            />
          </div>
          {mode === 'student' && (
            <>
              <div className="form-group">
                <label>Ученик</label>
                <select
                  className="form-select"
                  value={studentId}
                  onChange={(e) => setStudentId(e.target.value)}
                  disabled={studentsLoading}
                >
                  <option value="">Выберите ученика</option>
                  {students.map((s) => (
                    <option key={s.id} value={s.id}>
                      {studentLabelWithLogin(s)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Дедлайн</label>
                <input
                  type="date"
                  className="form-input"
                  value={deadline}
                  onChange={(e) => setDeadline(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Порядок</label>
                <div className="checkbox-row">
                  <label title="Случайный порядок вопросов у ученика">
                    <input
                      type="checkbox"
                      checked={shuffleQuestions}
                      onChange={(e) => setShuffleQuestions(e.target.checked)}
                    />
                    Перемешать
                  </label>
                </div>
              </div>
            </>
          )}
          {mode === 'template' && (
            <div className="form-group">
              <label>Теги заготовки (опционально)</label>
              <input
                type="text"
                className="form-input"
                placeholder="ID тегов через запятую"
                value={templateTagIds.join(', ')}
                onChange={(e) => setTemplateTagIds(e.target.value.split(',').map((x) => Number(x.trim())).filter((n) => !Number.isNaN(n)))}
              />
            </div>
          )}
          <div className="form-actions">
            {saveError && <span className="alert alert-error">{saveError}</span>}
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Сохранение…' : 'Сохранить'}
            </button>
          </div>
        </div>
      </div>

      <div className="teacher-list-create-columns">
        <aside className="teacher-list-create-left">
          <h3>Задачи</h3>
          <div className="teacher-list-create-filter">
            <input
              type="text"
              className="form-input"
              placeholder="Поиск по названию, условию, slug"
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchTasks()}
            />
            <select
              className="form-select"
              value={filterTag}
              onChange={(e) => setFilterTag(e.target.value)}
            >
              <option value="">Все теги</option>
              {tags.map((t) => (
                <option key={t.id} value={t.slug}>{t.name}</option>
              ))}
            </select>
            <select
              className="form-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              {TASK_STATUS_OPTIONS.map((o) => (
                <option key={o.value || 'any'} value={o.value}>{o.label}</option>
              ))}
            </select>
            <button type="button" className="btn btn-outline btn-sm" onClick={fetchTasks}>
              Применить
            </button>
          </div>
          <div className="teacher-list-create-template-select">
            <label>Взять из заготовки</label>
            <select
              className="form-select"
              value={selectedTemplateId}
              onChange={(e) => setSelectedTemplateId(e.target.value)}
              disabled={templatesLoading}
            >
              <option value="">—</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.title} ({Array.isArray(t.items) ? t.items.length : 0} задач)
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={loadFromTemplate}
              disabled={!selectedTemplateId || templatesLoading}
            >
              Загрузить в список
            </button>
          </div>
          <div className="teacher-list-create-left-actions">
            <button type="button" className="btn btn-xs btn-outline" onClick={expandAllLeft}>
              Развернуть все
            </button>
            <button type="button" className="btn btn-xs btn-outline" onClick={collapseAllLeft}>
              Свернуть все
            </button>
          </div>
          {tasksError && <div className="alert alert-error">{tasksError}</div>}
          {tasksLoading ? (
            <p className="text-secondary">Загрузка задач…</p>
          ) : tasks.length === 0 ? (
            <p className="text-secondary">Задач не найдено. Измените фильтр и нажмите «Применить».</p>
          ) : (
            <ul className="teacher-list-create-task-cards">
              {tasks.map((task) => {
                const inDraft = draftTaskIds.has(task.id);
                const collapsed = leftCollapsed[task.id] !== undefined ? leftCollapsed[task.id] : !leftExpandAll;
                const num = task.source_number || task.slug || `#${task.id}`;
                const tagNames = (task.tags || []).map((t) => t.name || t.slug).join(', ');
                return (
                  <li
                    key={task.id}
                    className={`teacher-task-card ${inDraft ? 'teacher-task-card-in-draft' : ''}`}
                  >
                    <div
                      className="teacher-task-card-header"
                      onClick={() => !inDraft && setLeftCardCollapsed(task.id, !collapsed)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => !inDraft && (e.key === 'Enter' || e.key === ' ') && setLeftCardCollapsed(task.id, !collapsed)}
                    >
                      <span className="teacher-task-card-num">{num}</span>
                      {tagNames && <span className="teacher-task-card-tags">{tagNames}</span>}
                      {!inDraft && (
                        <span className="teacher-task-card-toggle">{collapsed ? '▼' : '▲'}</span>
                      )}
                      {!inDraft && (
                        <button
                          type="button"
                          className="btn btn-xs btn-primary"
                          onClick={(e) => { e.stopPropagation(); addToDraft(task); }}
                        >
                          Добавить
                        </button>
                      )}
                      {inDraft && <span className="teacher-task-card-badge">В списке</span>}
                    </div>
                    {!collapsed && (
                      <div className="teacher-task-card-body">
                        <div className="teacher-task-card-statement">
                          <MarkdownText text={task.statement || '—'} />
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </aside>
        <main className="teacher-list-create-right">
          <h3>Формируемый список</h3>
          <div className="teacher-list-create-right-actions">
            <button type="button" className="btn btn-xs btn-outline" onClick={expandAllRight}>
              Развернуть все
            </button>
            <button type="button" className="btn btn-xs btn-outline" onClick={collapseAllRight}>
              Свернуть все
            </button>
          </div>
          {draftItems.length === 0 ? (
            <p className="text-secondary">Добавьте задачи из левой колонки кнопкой «Добавить» или загрузите из заготовки.</p>
          ) : (
            <ol className="teacher-list-create-draft-list">
              {draftItems.map((it, index) => (
                <li key={`${it.taskId}-${index}`} className="teacher-draft-item">
                  <div className="teacher-draft-item-header">
                    <span className="teacher-draft-item-num">{index + 1}.</span>
                    <span className="teacher-draft-item-title">{it.source_number || it.slug || it.title || `#${it.taskId}`}</span>
                    {(it.tags || []).length > 0 && (
                      <span className="teacher-draft-item-tags">
                        {(it.tags || []).map((t) => t.name || t.slug).join(', ')}
                      </span>
                    )}
                    <button
                      type="button"
                      className="btn btn-xs btn-outline"
                      onClick={() => setDraftItemCollapsed(it.taskId, !it.collapsed)}
                    >
                      {it.collapsed ? 'Развернуть' : 'Свернуть'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-xs btn-outline"
                      onClick={() => moveDraftItem(index, -1)}
                      disabled={index === 0}
                      title="Выше"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="btn btn-xs btn-outline"
                      onClick={() => moveDraftItem(index, 1)}
                      disabled={index === draftItems.length - 1}
                      title="Ниже"
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="btn btn-xs btn-outline"
                      onClick={() => removeFromDraft(it.taskId)}
                      title="Удалить"
                    >
                      ✕
                    </button>
                  </div>
                  {!it.collapsed && (
                    <div className="teacher-draft-item-body">
                      <div className="teacher-draft-statement">
                        <MarkdownText text={it.statement || '—'} />
                      </div>
                      <div className="form-group">
                        <label>Комментарий преподавателя</label>
                        <input
                          type="text"
                          className="form-input"
                          value={it.teacherNote}
                          onChange={(e) => updateDraftNote(it.taskId, e.target.value)}
                          placeholder="Необязательно"
                        />
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ol>
          )}
        </main>
      </div>
    </div>
  );
}
